/**
 * DataSetColumn.tsx - Resizable Dataset Column with Field Mappings
 * 
 * CHANGE HISTORY:
 * - 251128_1147 danielbeer: Created dataset column component
 * 
 * Features:
 * - Resizable panel using Panelr
 * - Field list with 1:1 mapping arrows
 * - Add/edit/remove fields
 * - Transform field support
 * - Value propagation from source
 */

import React, { useState, useCallback, useRef } from 'react';
import type { DataSet, Field, FieldMapping } from '../types/index';
import { FieldMappingRow } from './FieldMappingRow';
import { FieldEditorDialog } from './FieldEditorDialog';
import { usePragmaticDraggable, usePragmaticDropTarget } from '@opcentral/draggr';
import './DataSetColumn.css';

export interface DataSetColumnProps {
  dataset: DataSet;
  previousDataset?: DataSet;
  showFieldTypes: boolean;
  showSampleData: boolean;
  compact: boolean;
  isActive: boolean;
  onDatasetChange: (updates: Partial<DataSet>) => void;
  onRemove?: () => void;
  onFieldFocus?: (fieldId: string) => void;
  onReorder?: (datasetId: string, newPosition: number) => void;
}

/**
 * DataSetColumn - Single dataset in the pipeline
 */
export const DataSetColumn: React.FC<DataSetColumnProps> = ({
  dataset,
  previousDataset,
  showFieldTypes,
  showSampleData,
  compact,
  isActive,
  onDatasetChange,
  onRemove,
  onFieldFocus,
  onReorder
}) => {
  const [editingName, setEditingName] = useState(false);
  const [showFieldEditor, setShowFieldEditor] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isDropTarget, setIsDropTarget] = useState(false);
  const columnRef = useRef<HTMLDivElement>(null);
  const [nameValue, setNameValue] = useState(dataset.name);

  // Setup drag and drop using Draggr hooks
  usePragmaticDraggable(columnRef, {
    type: 'dataset',
    data: { datasetId: dataset.id, position: dataset.position },
    onDragStart: () => setIsDragging(true),
    onDrop: () => setIsDragging(false),
  });

  usePragmaticDropTarget(columnRef, {
    accepts: ['dataset'],
    onDragEnter: () => setIsDropTarget(true),
    onDragLeave: () => setIsDropTarget(false),
    onDrop: ({ source }) => {
      setIsDropTarget(false);
      const sourcePosition = source.data.position as number;
      if (sourcePosition !== dataset.position && onReorder) {
        onReorder(source.data.datasetId as string, dataset.position);
      }
    },
  });

  // Handle field changes
  const handleFieldsChange = useCallback((newFields: Field[]) => {
    // Update mappings to match new fields
    const newMappings: FieldMapping[] = newFields.map(field => {
      const existingMapping = dataset.mappings.find(m => m.targetFieldId === field.id);
      if (existingMapping) return existingMapping;
      
      // Create new mapping
      return {
        id: `mapping-${field.id}`,
        sourceFieldId: null,
        targetFieldId: field.id,
        enabled: false,
        hasMatch: false
      };
    });

    onDatasetChange({ fields: newFields, mappings: newMappings });
  }, [dataset.mappings, onDatasetChange]);

  // Handle mapping changes
  const handleMappingChange = useCallback((mappingId: string, updates: Partial<FieldMapping>) => {
    const newMappings = dataset.mappings.map(m =>
      m.id === mappingId ? { ...m, ...updates } : m
    );
    onDatasetChange({ mappings: newMappings });
  }, [dataset.mappings, onDatasetChange]);

  // Handle name change
  const handleNameSave = useCallback(() => {
    onDatasetChange({ name: nameValue });
    setEditingName(false);
  }, [nameValue, onDatasetChange]);

  // Add transform field
  const handleAddTransformField = useCallback(() => {
    const newField: Field = {
      id: `field-${Date.now()}`,
      name: 'New Transform',
      type: 'transform',
      description: 'Transform field'
    };

    const newMapping: FieldMapping = {
      id: `mapping-${newField.id}`,
      sourceFieldId: null,
      targetFieldId: newField.id,
      enabled: false,
      hasMatch: false,
      transform: {
        type: 'regex',
        pattern: '.*',
        flags: 'g'
      }
    };

    onDatasetChange({
      fields: [...dataset.fields, newField],
      mappings: [...dataset.mappings, newMapping]
    });
  }, [dataset.fields, dataset.mappings, onDatasetChange]);

  // Auto-match fields from previous dataset
  const handleAutoMatch = useCallback(() => {
    if (!previousDataset) return;

    const newMappings = dataset.mappings.map(mapping => {
      const targetField = dataset.fields.find(f => f.id === mapping.targetFieldId);
      if (!targetField) return mapping;

      // Try to find matching source field by name (case-insensitive)
      const matchingSource = previousDataset.fields.find(
        f => f.name.toLowerCase() === targetField.name.toLowerCase()
      );

      if (matchingSource) {
        return {
          ...mapping,
          sourceFieldId: matchingSource.id,
          enabled: true,
          hasMatch: true
        };
      }

      // Try partial match (contains)
      const partialMatch = previousDataset.fields.find(
        f => f.name.toLowerCase().includes(targetField.name.toLowerCase()) ||
             targetField.name.toLowerCase().includes(f.name.toLowerCase())
      );

      if (partialMatch) {
        return {
          ...mapping,
          sourceFieldId: partialMatch.id,
          enabled: true,
          hasMatch: true
        };
      }

      return mapping;
    });

    onDatasetChange({ mappings: newMappings });
  }, [dataset.fields, dataset.mappings, previousDataset, onDatasetChange]);

  // Handle creating a mapping from another dataset
  const handleCreateMapping = useCallback((sourceFieldId: string, targetFieldId: string) => {
    // Find the target field's existing mapping
    const targetMapping = dataset.mappings.find(m => m.targetFieldId === targetFieldId);
    
    if (targetMapping) {
      // Update existing mapping to point to new source
      const updatedMappings = dataset.mappings.map(m => 
        m.id === targetMapping.id
          ? { ...m, sourceFieldId, enabled: true, hasMatch: true }
          : m
      );
      onDatasetChange({ mappings: updatedMappings });
    } else {
      // Create new mapping
      const newMapping: FieldMapping = {
        id: `mapping-${targetFieldId}-${Date.now()}`,
        sourceFieldId,
        targetFieldId,
        enabled: true,
        hasMatch: true,
      };
      onDatasetChange({ mappings: [...dataset.mappings, newMapping] });
    }
  }, [dataset.mappings, onDatasetChange]);

  // Handle field reordering
  const handleFieldReorder = useCallback((fieldId: string, newIndex: number) => {
    const oldIndex = dataset.fields.findIndex(f => f.id === fieldId);
    if (oldIndex === -1 || oldIndex === newIndex) return;

    const newFields = [...dataset.fields];
    const [movedField] = newFields.splice(oldIndex, 1);
    newFields.splice(newIndex, 0, movedField);

    onDatasetChange({ fields: newFields });
  }, [dataset.fields, onDatasetChange]);

  // Get propagated value for a field
  const getPropagatedValue = useCallback((mapping: FieldMapping): any => {
    if (!mapping.enabled || !mapping.sourceFieldId || !previousDataset) {
      return undefined;
    }

    const sourceField = previousDataset.fields.find(f => f.id === mapping.sourceFieldId);
    if (!sourceField) return undefined;

    // If previous dataset has data, get the first record's value
    if (previousDataset.data && previousDataset.data.length > 0) {
      const firstRecord = previousDataset.data[0];
      return firstRecord[sourceField.name];
    }

    // Otherwise use the sample value
    return sourceField.sample;
  }, [previousDataset]);

  return (
    <div 
      ref={columnRef}
      className={`dataset-column ${isActive ? 'dataset-column--active' : ''} ${compact ? 'dataset-column--compact' : ''} ${isDragging ? 'dataset-column--dragging' : ''} ${isDropTarget ? 'dataset-column--drop-target' : ''}`}
      style={{ width: dataset.width || 300 }}
    >
      {/* Header */}
      <div className="dataset-column__header">
        {editingName ? (
          <input
            type="text"
            className="dataset-column__name-input"
            value={nameValue}
            onChange={(e) => setNameValue(e.target.value)}
            onBlur={handleNameSave}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleNameSave();
              if (e.key === 'Escape') {
                setNameValue(dataset.name);
                setEditingName(false);
              }
            }}
            autoFocus
          />
        ) : (
          <h3 
            className="dataset-column__name"
            onClick={() => setEditingName(true)}
            title="Click to edit name"
          >
            {dataset.name}
          </h3>
        )}
        
        <div className="dataset-column__header-controls">
          <button
            className={`dataset-column__enable-btn ${dataset.enabled !== false ? 'enabled' : 'disabled'}`}
            onClick={() => onDatasetChange({ enabled: !(dataset.enabled !== false) })}
            title={dataset.enabled !== false ? 'Disable dataset' : 'Enable dataset'}
          >
            {dataset.enabled !== false ? '✓' : '○'}
          </button>
          <span className="dataset-column__count">{dataset.fields.length}</span>
          {onRemove && (
            <button
              className="dataset-column__remove-btn"
              onClick={onRemove}
              title="Remove dataset"
            >
              ×
            </button>
          )}
        </div>
      </div>

      {/* Field list */}
      <div className="dataset-column__fields">
        {dataset.fields.length === 0 ? (
          <div className="dataset-column__empty">
            <p>No fields</p>
            <button onClick={() => setShowFieldEditor(true)}>
              + Add Fields
            </button>
          </div>
        ) : (
          dataset.fields.map((field, index) => {
            const mapping = dataset.mappings.find(m => m.targetFieldId === field.id);
            const propagatedValue = mapping ? getPropagatedValue(mapping) : undefined;

            return (
              <FieldMappingRow
                key={field.id}
                field={field}
                mapping={mapping}
                previousDataset={previousDataset}
                showFieldType={showFieldTypes}
                showSampleData={showSampleData}
                propagatedValue={propagatedValue}
                compact={compact}
                onMappingChange={(updates: Partial<FieldMapping>) => mapping && handleMappingChange(mapping.id, updates)}
                onFieldFocus={() => onFieldFocus?.(field.id)}
                onReorder={handleFieldReorder}
                onCreateMapping={handleCreateMapping}
                datasetId={dataset.id}
                index={index}
              />
            );
          })
        )}
      </div>

      {/* Footer controls */}
      <div className="dataset-column__footer">
        <button
          className="dataset-column__edit-btn"
          onClick={() => setShowFieldEditor(true)}
          title="Edit fields"
        >
          ✏️ Edit
        </button>
        {previousDataset && (
          <>
            <button
              className="dataset-column__auto-match-btn"
              onClick={handleAutoMatch}
              title="Auto-match fields from previous dataset"
            >
              🎯 Auto-Match
            </button>
            <button
              className="dataset-column__transform-btn"
              onClick={handleAddTransformField}
              title="Add transform field"
            >
              + Transform
            </button>
          </>
        )}
      </div>

      {/* Field editor dialog */}
      {showFieldEditor && (
        <FieldEditorDialog
          fields={dataset.fields}
          onSave={handleFieldsChange}
          onClose={() => setShowFieldEditor(false)}
        />
      )}
    </div>
  );
};
